import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LedgerEntryLine } from '../clients/rust-api-client';
import { memberBillingService } from '../clients/ApiClients';
import { Button } from '../Components/Catalyst/button';
import { Text } from '../Components/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import {
  billingErrorMessage,
  entryTypeFilter,
  formatBillingDate,
  formatMoney,
  signedAmountParts,
} from '../utils/billingHelpers';
import { ActivityFilter, entryLabel, filterTypes } from './entryLabels';

interface Props {
  homeId: string;
  /** Cap the rows and hide the controls — the hub's preview mode. */
  limit?: number;
  filter?: ActivityFilter;
  onSelect?: (entry: LedgerEntryLine) => void;
  onError?: (message: string) => void;
}

const PAGE_SIZE = 25;

/** `"2026-08-23"` → `"August 2026"`, without constructing a UTC date. */
export const monthLabel = (effectiveDate: string): string => {
  const match = /^(\d{4})-(\d{2})/.exec(effectiveDate.trim());
  if (!match) return effectiveDate;
  const [, year, month] = match;
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(
    undefined,
    { month: 'long', year: 'numeric' }
  );
};

/** Rows grouped into the months they fall in, order preserved. */
export const groupByMonth = (
  entries: ReadonlyArray<LedgerEntryLine>
): Array<{ month: string; entries: LedgerEntryLine[] }> => {
  const groups: Array<{ month: string; entries: LedgerEntryLine[] }> = [];
  entries.forEach((entry) => {
    const month = monthLabel(entry.effective_date);
    const last = groups[groups.length - 1];
    if (last && last.month === month) {
      last.entries.push(entry);
    } else {
      groups.push({ month, entries: [entry] });
    }
  });
  return groups;
};

/**
 * The account's activity, as a feed rather than a ledger table.
 *
 * Three things differ from front-porch's `HomeLedgerTable`, and each is
 * a deliberate answer to "who is reading this":
 *
 * **It is rows, not a seven-column table.** The homeowner persona is
 * mobile-first; a dense table with Date / Type / Description / Due /
 * Amount / Balance / action columns does not survive a phone.
 *
 * **The running balance is hidden while a filter is on.** front-porch
 * explains the paradox instead — *"These are the account's real
 * figures, including entries a filter is hiding"* — which is true, and
 * which a homeowner should never have to work through. The invariant is
 * untouched: `running_balance` still comes from the whole ledger and is
 * never re-summed here. It is simply not shown next to a subset of
 * rows, because a balance beside a filtered list answers a question
 * nobody asked.
 *
 * **A reversed entry says so.** An entry something reverses is struck
 * through and a reversal names what it undoes — either row alone reads
 * as a mistake, and the pair is the story.
 */
const ActivityFeed: React.FC<Props> = ({
  homeId,
  limit,
  filter = 'all',
  onSelect,
  onError,
}) => {
  const [entries, setEntries] = useState<LedgerEntryLine[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const perPage = limit ?? PAGE_SIZE;
  const isPreview = limit != null;
  const filtered = filter !== 'all';

  const load = useCallback(
    async (pageNo: number): Promise<void> => {
      try {
        if (pageNo === 1) setLoading(true);
        else setLoadingMore(true);

        const { data } = await memberBillingService.getMyLedger({
          homeId,
          page: pageNo,
          perPage,
          order: 'desc',
          entryType: entryTypeFilter(filterTypes(filter)),
        });

        setTotal(data.total);
        setEntries((prev) =>
          pageNo === 1 ? data.entries : [...prev, ...data.entries]
        );
      } catch (err) {
        onError?.(
          billingErrorMessage(err, "Couldn't load your account activity.")
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [homeId, perPage, filter, onError]
  );

  useEffect(() => {
    setPage(1);
    void load(1);
  }, [load]);

  /** Entry ids that something on screen reverses. */
  const reversedIds = useMemo(() => {
    const ids = new Set<string>();
    entries.forEach((e) => {
      if (e.reverses_entry_id) ids.add(e.reverses_entry_id);
    });
    return ids;
  }, [entries]);

  const groups = useMemo(() => groupByMonth(entries), [entries]);
  const hasMore = !isPreview && entries.length < total;

  const loadMore = (): void => {
    const next = page + 1;
    setPage(next);
    void load(next);
  };

  if (loading) return <LoadingSpinner />;

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 p-6 dark:border-zinc-700">
        <Text>
          {filtered
            ? 'Nothing here under this filter. Switch back to All to see your whole account.'
            : 'Nothing has been posted to your account yet.'}
        </Text>
      </div>
    );
  }

  return (
    <div>
      {groups.map((group) => (
        <div key={group.month} className="mb-6 last:mb-0">
          <div className="sticky top-0 z-10 bg-stone-300/90 py-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-600 backdrop-blur dark:bg-gray-900/90 dark:text-zinc-400">
            {group.month}
          </div>

          <ul className="divide-y divide-zinc-200 overflow-hidden rounded-xl border border-zinc-200 bg-white dark:divide-zinc-700 dark:border-zinc-700 dark:bg-gray-800">
            {group.entries.map((entry) => {
              const parts = signedAmountParts(entry.amount);
              const wasReversed = reversedIds.has(entry.id);
              const isReversal = !!entry.reverses_entry_id;

              const row = (
                <div className="flex w-full items-start justify-between gap-3 px-4 py-3.5 text-left">
                  <div className="min-w-0 flex-1">
                    <div
                      className={`truncate text-sm font-medium text-zinc-900 dark:text-white ${
                        wasReversed ? 'line-through' : ''
                      }`}
                    >
                      {entryLabel(entry.entry_type)}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-zinc-500 dark:text-zinc-400">
                      {formatBillingDate(entry.effective_date)}
                      {entry.description ? ` · ${entry.description}` : ''}
                    </div>
                    {isReversal && (
                      <div className="mt-0.5 text-xs text-purple-700 dark:text-purple-400">
                        Corrects an earlier entry — the two cancel out.
                      </div>
                    )}
                    {wasReversed && (
                      <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                        Corrected — this no longer affects your balance.
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 text-right">
                    <div
                      className={`text-sm font-semibold tabular-nums ${
                        parts.isCredit
                          ? 'text-lime-700 dark:text-lime-400'
                          : 'text-zinc-900 dark:text-white'
                      }`}
                    >
                      {parts.text}
                    </div>
                    {/* Only on the unfiltered feed — see the note above. */}
                    {!filtered && (
                      <div className="mt-0.5 text-xs tabular-nums text-zinc-400 dark:text-zinc-400">
                        {formatMoney(entry.running_balance)}
                      </div>
                    )}
                  </div>
                </div>
              );

              return (
                <li key={entry.id}>
                  {onSelect ? (
                    <button
                      type="button"
                      onClick={() => onSelect(entry)}
                      className="w-full hover:bg-zinc-50 focus:bg-zinc-50 focus:outline-none dark:hover:bg-gray-700/50 dark:focus:bg-gray-700/50"
                    >
                      {row}
                    </button>
                  ) : (
                    row
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <Button plain onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load earlier activity'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
